import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, map, Observable, startWith, Subject, takeUntil } from 'rxjs';
import { RallyanceReclamationSQLResponse } from 'src/app/Interfaces/reclamations';
import { AlertService } from 'src/app/services/alert.service';
import { AppStyleService } from 'src/app/services/app-style.service';
import { IndexedDBService } from 'src/app/services/indexed-db.service';
import { LoadingService } from 'src/app/services/loading.service';
import { ReclamationService } from 'src/app/services/reclamation.service';
import { StoreService } from 'src/app/services/store.service';
import { TextService } from 'src/app/services/text.service';
import { UtilService } from 'src/app/services/util.service';
import { genericSearchingFunctionWithoutNullChecks } from 'src/app/Utility/functions';
import { environment } from 'src/environments/environment';

@Component({
	selector: 'app-reclamation-tasks',
	templateUrl: './reclamation-tasks.component.html',
	styleUrls: ['./reclamation-tasks.component.scss'],
})
export class ReclamationTasksComponent implements OnInit, OnDestroy {
	constructor(public _ss: StoreService, private _fb: FormBuilder, private router: Router, public _text: TextService, private _util: UtilService, private _reclamation: ReclamationService) {}

	form = this._fb.nonNullable.group({
		ClientSearch: [''],
	});
	dateString = Intl.DateTimeFormat('he-il', { year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' }).format(+new Date());
	windowWidth = innerWidth;

	tasks$ = this._reclamation.allTasks$;
	tasksDisplay$ = combineLatest([this.tasks$, this.form.controls.ClientSearch.valueChanges.pipe(startWith(''))]).pipe(
		map(([tasks, search]) => {
			if (!search) return tasks;
			return genericSearchingFunctionWithoutNullChecks(search, tasks, ['ClientId', 'Client_Name', 'RekID', 'ProductId']);
		}),
	);
	totalTasks$ = this.tasks$.pipe(map(tasks => tasks.length));
	totalTasksCompleted$ = this.tasks$.pipe(map(tasks => tasks.filter(task => !!task.RekStatus).length));

	destroy$ = new Subject<void>();

	zoomLevel = this.windowWidth > 767 && this.windowWidth < 1099 ? 1.2 : 1;

	itemSize = this.windowWidth > 768 ? (this.windowWidth > 1099 ? 146 : 106) : 82;

	isLocal = !environment.production;

	hasReports = false;

	Admin = '';

	isBarcodePopupOpen = false;
	supportsVideo = true;
	@ViewChild('videoElement') videoElement: ElementRef<HTMLVideoElement>;
	video: HTMLVideoElement;
	mediaStream: MediaStream;
	detectedCode = '';
	intervalID;

	ngOnInit(): void {
		this._text.store.mainTasks = 'משימות רקלמציה';

		this.getReclamationTasks();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	BackToHome() {
		this.router.navigate(['home']);
	}

	ClearClientSearch() {
		this.form.controls.ClientSearch.reset();
	}

	handleClicked(task: RallyanceReclamationSQLResponse) {
		this._reclamation.selectNewTask(task);
		this._reclamation.navigateToReclamatorTasks('finish');
	}

	getReclamationTasks() {
		// if (this._reclamation.allTasks$.value.length) {
		// 	return;
		// }

		this.handleUpdateTasks();
	}

	handleUpdateTasks() {
		this._ss.closeMenu();

		this.pipeADestroyer(this._reclamation.getReclamationTasks()).subscribe({
			next: res => {
				if (typeof res === 'string') {
					alert('ישנה תקלת תקשורת, נסה שנית מאוחר יותר');
					history.back();
					return;
				}
				this.tasks$.next(res.recordset);
			},
			error: err => {
				alert('ישנה תקלת תקשורת, נסה שנית מאוחר יותר');
				console.error(err);

				history.back();
				return;
			},
		});
	}

	handleBarcodePopupOpen() {
		this.isBarcodePopupOpen = true;

		setTimeout(() => {
			try {
				if (this.videoElement && this.videoElement.nativeElement) {
					this.video = this.videoElement.nativeElement;
				} else {
					alert('no video element');
					throw new Error();
				}

				if (!window.BarcodeDetector) {
					alert('קורא ברקוד אינו נתמך במכשיר זה');
					throw new Error();
				}

				if (!window.navigator.mediaDevices || !('getUserMedia' in window.navigator.mediaDevices)) {
					alert('אין גישה למצלמה');
					throw new Error();
				}
			} catch (error) {
				this.handleBarcodePopupClose();
				return;
			}

			const constraints: MediaStreamConstraints = {
				video: { facingMode: { exact: 'environment' } },
				audio: false,
			};

			window.navigator.mediaDevices
				.getUserMedia(constraints)
				.then(async stream => {
					this.video.srcObject = stream;
					this.video.play();

					this.mediaStream = stream;
					const formats: SupportedFormats[] = ['code_39', 'codabar', 'ean_13', 'code_128'];

					const barcodeFormatsDetect = new window.BarcodeDetector({ formats });

					if (!barcodeFormatsDetect) {
						alert('קורא ברקוד אינו נתמך במכשיר זה');
						return;
					}

					const barcodeDetector = new window.BarcodeDetector({ formats });

					// Detect code function
					const detectCode = async () => {
						if (!('detect' in barcodeDetector) || !this.video || this.video.paused) {
							return;
						}

						if (this.detectedCode?.length) {
							return;
						}

						try {
							const codes = await barcodeDetector.detect(this.video);
							if (!codes?.length) return;

							for (const barcode of codes) {
								this.detectedCode = barcode.rawValue;

								const searchControl = this.form.controls.ClientSearch;

								searchControl.setValue(barcode.rawValue);
							}
						} catch (error) {
							console.error(error);
						}

						this.handleBarcodePopupClose();
					};

					this.intervalID = setInterval(detectCode, 200);
				})
				.catch(err => {
					this.supportsVideo = false;
					alert('יש לאפשר גישה למצלמה כדי להפעיל סורק ברקוד');
					this.handleBarcodePopupClose();
				});
		}, 0);
	}

	handleBarcodePopupClose() {
		if (this.mediaStream) {
			for (const track of this.mediaStream.getTracks()) {
				this.mediaStream.removeTrack(track);
			}
		}
		if (this.video) {
			this.video.srcObject = null;
			this.video.pause();
		}

		clearInterval(this.intervalID);

		this.detectedCode = '';

		this.isBarcodePopupOpen = false;
	}

	pipeADestroyer<T>(method: Observable<T>) {
		return this._util.pipeASubjectDestoryer(method, this.destroy$);
	}
}
